import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { DropdownButton, MenuItem } from 'react-bootstrap';
import {
  Camera,
  FileText,
  Film,
  Layers,
  MoreHorizontal,
  Music,
  Play,
} from 'lucide-react';
import Thumbnail from 'components/Thumbnails/Thumbnail';
import AlertBox from 'components/AlertBox/AlertBox';
import Markdown from 'components/Shared/Markdown';
import { deleteProject } from 'redux/modules/portfolio';
import { showActionToast } from 'components/ActionToast';
import { removeMarkdownSyntax } from 'helpers/HelperFunctions';
import { isPlayableProject } from 'helpers/projectMedia';
import PortfolioProjectPlayer from './PortfolioProjectPlayer';
import styles from './PortfolioProjects.module.scss';

const MAX_ROLE_CHIPS = 3;

const getTypeIcon = (project) => {
  if (project.collection) return Layers;
  if (project.mediaType === 'video') return Film;
  if (project.mediaType === 'audio') return Music;
  if (project.mediaType === 'image') return Camera;
  return FileText;
};

const PortfolioProjectCard = ({
  project,
  roles,
  isOwn,
  user,
  disabled,
  showMoveToTop,
  showMoveUp,
  showMoveDown,
  onMoveToTop,
  onMoveUp,
  onMoveDown,
  className,
  draggable,
  isDragging,
  isDropTarget,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  isPlaying,
  onPlay,
  onStopPlaying,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  if (!project) {
    return null;
  }

  const TypeIcon = getTypeIcon(project);
  const isText = project.mediaType === 'text';
  const canPlay = !!onPlay && isPlayableProject(project);
  const description = project.description
    ? removeMarkdownSyntax(project.description)
    : '';
  const shownRoles = roles.filter(Boolean);
  const extraRoles = shownRoles.length - MAX_ROLE_CHIPS;

  const handleConfirmDelete = () => {
    if (deleting) return;

    setDeleting(true);
    dispatch(deleteProject(project.id))
      .then(() => {
        setShowDeleteModal(false);
        window.location.href = `/${user.username}`;
      })
      .catch((error) => {
        console.error('Error deleting project:', error);
        setDeleting(false);
        setShowDeleteModal(false);
        showActionToast({
          message: "We couldn't delete that project. Try again.",
        });
      });
  };

  // The whole card opens the project. Clicks on its own links, the owner menu
  // or the delete dialog are left alone.
  const handleCardClick = (event) => {
    if (
      event.target.closest(
        'a, button, [role="menu"], .modal, [data-card-player]'
      )
    ) {
      return;
    }
    history.push(project.links.main_relative);
  };

  // Drag the whole card, whichever link or image the drag started on.
  const handleDragStart = (event) => {
    if (event.dataTransfer.setDragImage) {
      const rect = event.currentTarget.getBoundingClientRect();
      event.dataTransfer.setDragImage(
        event.currentTarget,
        event.clientX - rect.left,
        event.clientY - rect.top
      );
    }
    onDragStart(event);
  };

  const handleMenuSelect = (callback) => (event) => {
    if (event && event.stopPropagation) {
      event.stopPropagation();
    }
    // Still reachable by keyboard, or from a menu opened before the reorder.
    if (disabled) return;
    callback();
  };

  return (
    <article
      className={`${styles.card} ${draggable ? styles.draggable : ''} ${
        isDragging ? styles.dragging : ''
      } ${isDropTarget ? styles.dropTarget : ''} ${className || ''}`}
      style={disabled ? { opacity: 0.7, pointerEvents: 'none' } : undefined}
      onClick={handleCardClick}
      draggable={draggable || undefined}
      onDragStart={draggable ? handleDragStart : undefined}
      onDragOver={draggable ? onDragOver : undefined}
      onDrop={draggable ? onDrop : undefined}
      onDragEnd={draggable ? onDragEnd : undefined}
    >
      {isPlaying ? (
        <div className={styles.media}>
          <PortfolioProjectPlayer project={project} onClose={onStopPlaying} />
        </div>
      ) : isText || !project.thumbnails ? (
        // Not a link: the Markdown can hold links of its own. The card click
        // still opens the project.
        <div className={`${styles.media} ${styles.textPreview}`}>
          <Markdown text={project.description || ''} />
        </div>
      ) : (
        <div className={styles.media}>
          <Link
            to={project.links.main_relative}
            className={styles.mediaLink}
            tabIndex={-1}
            aria-hidden="true"
          >
            <Thumbnail
              aspectRatio="16:9"
              thumbnails={project.thumbnails}
              style={{ width: '100%', height: '100%' }}
            />
          </Link>
          {canPlay && (
            <button
              type="button"
              className={styles.playButton}
              onClick={onPlay}
              aria-label={`Play ${project.title}`}
            >
              <Play size={22} strokeWidth={2} fill="currentColor" />
            </button>
          )}
        </div>
      )}
      {isOwn && (
        <div className={styles.menu}>
          <DropdownButton
            id={`project-actions-${project.id}`}
            noCaret
            pullRight
            bsStyle="default"
            className={styles.menuButton}
            title={
              <MoreHorizontal
                size={16}
                strokeWidth={2}
                aria-label="Project options"
              />
            }
          >
            <MenuItem href={project.links.edit}>Edit</MenuItem>
            <MenuItem onClick={() => setShowDeleteModal(true)}>Delete</MenuItem>
            {(showMoveUp || showMoveDown || showMoveToTop) && (
              <MenuItem divider />
            )}
            {showMoveToTop && (
              <MenuItem onClick={handleMenuSelect(onMoveToTop)}>
                Move to top
              </MenuItem>
            )}
            {showMoveUp && (
              <MenuItem onClick={handleMenuSelect(onMoveUp)}>Move up</MenuItem>
            )}
            {showMoveDown && (
              <MenuItem onClick={handleMenuSelect(onMoveDown)}>
                Move down
              </MenuItem>
            )}
          </DropdownButton>
        </div>
      )}
      <div className={styles.body}>
        <div className={styles.titleRow}>
          <TypeIcon
            className={styles.typeIcon}
            size={16}
            strokeWidth={2}
            aria-hidden="true"
          />
          <Link to={project.links.main_relative} className={styles.title}>
            {project.title}
          </Link>
        </div>
        {description && !isText && (
          <p className={styles.description}>{description}</p>
        )}
        {shownRoles.length > 0 && (
          <ul className={styles.roles} aria-label="Roles">
            {shownRoles.slice(0, MAX_ROLE_CHIPS).map((role) => (
              <li key={role.id || role.name} className={styles.role}>
                {role.name}
              </li>
            ))}
            {extraRoles > 0 && <li className={styles.role}>+{extraRoles}</li>}
          </ul>
        )}
      </div>
      <AlertBox
        show={showDeleteModal}
        title="Delete project"
        text="Are you sure you want to delete this project?"
        confirmButtonText="Yes, delete it"
        onConfirm={handleConfirmDelete}
        onCancel={() => setShowDeleteModal(false)}
        disableConfirmButton={deleting}
      />
    </article>
  );
};

PortfolioProjectCard.propTypes = {
  project: PropTypes.object,
  roles: PropTypes.array,
  isOwn: PropTypes.bool,
  user: PropTypes.object,
  disabled: PropTypes.bool,
  showMoveToTop: PropTypes.bool,
  showMoveUp: PropTypes.bool,
  showMoveDown: PropTypes.bool,
  onMoveToTop: PropTypes.func,
  onMoveUp: PropTypes.func,
  onMoveDown: PropTypes.func,
  className: PropTypes.string,
  draggable: PropTypes.bool,
  isDragging: PropTypes.bool,
  isDropTarget: PropTypes.bool,
  onDragStart: PropTypes.func,
  onDragOver: PropTypes.func,
  onDrop: PropTypes.func,
  onDragEnd: PropTypes.func,
  isPlaying: PropTypes.bool,
  onPlay: PropTypes.func,
  onStopPlaying: PropTypes.func,
};

PortfolioProjectCard.defaultProps = {
  roles: [],
};

export default PortfolioProjectCard;
